import { z } from 'zod';
import { t as tGlobal } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';

// Define IntendedUseOptions enum if not already imported
export enum IntendedUseOptions {
  GENERAL_OPERATING = 'GENERAL_OPERATING',
  SAVINGS_RESERVE = 'SAVINGS_RESERVE',
  PAYROLL_FACILITATION = 'PAYROLL_FACILITATION',
  MERCHANT_PAYMENT_FACILITATION = 'MERCHANT_PAYMENT_FACILITATION',
  CREDIT = 'CREDIT',
  OTHER = 'OTHER',
}

export const useIntendedUseLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');

  // Define options with their translation key getters
  const optionsList = [
    {
      value: IntendedUseOptions.GENERAL_OPERATING,
      getLabel: () => t('organic.intendedUse.generalOperating'),
    },
    {
      value: IntendedUseOptions.SAVINGS_RESERVE,
      getLabel: () => t('organic.intendedUse.savingsReserve'),
    },
    {
      value: IntendedUseOptions.PAYROLL_FACILITATION,
      getLabel: () => t('organic.intendedUse.payrollFacilitation'),
    },
    {
      value: IntendedUseOptions.MERCHANT_PAYMENT_FACILITATION,
      getLabel: () => t('organic.intendedUse.merchantPaymentFacilitation'),
    },
    {
      value: IntendedUseOptions.CREDIT,
      getLabel: () => t('organic.intendedUse.credit'),
    },
    {
      value: IntendedUseOptions.OTHER,
      getLabel: () => t('organic.intendedUse.other'),
    },
  ];

  const formMethods = useFormHook<{
    [EddEmbeddedFields.EDD_INTENDED_USE]: string;
    [EddEmbeddedFields.EDD_INTENDED_USE_OTHER_FREE_TEXT]: string;
  }>({
    schemaTitle: 'Intended Use',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_INTENDED_USE]: (() => {
        const savedValue = getDefaultValueByQuestionId(EddEmbeddedFields.EDD_INTENDED_USE);
        if (Array.isArray(savedValue)) return savedValue[0] || '';
        return savedValue || '';
      })(),
      [EddEmbeddedFields.EDD_INTENDED_USE_OTHER_FREE_TEXT]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_INTENDED_USE_OTHER_FREE_TEXT) || '',
    },
    formSchema: z
      .object({
        [EddEmbeddedFields.EDD_INTENDED_USE]: z
          .string()
          .min(1, { message: tGlobal('enErrors:required') }),
        [EddEmbeddedFields.EDD_INTENDED_USE_OTHER_FREE_TEXT]: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        // Check if OTHER option is selected
        const isOtherSelected =
          data[EddEmbeddedFields.EDD_INTENDED_USE] === IntendedUseOptions.OTHER;

        // Only validate OTHER_FREE_TEXT field when OTHER is selected
        if (isOtherSelected) {
          const freeText = data[EddEmbeddedFields.EDD_INTENDED_USE_OTHER_FREE_TEXT];

          if (!freeText || freeText.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tGlobal('enErrors:required'),
              path: [EddEmbeddedFields.EDD_INTENDED_USE_OTHER_FREE_TEXT],
            });
          } else if (freeText.length < 10) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(
                'organic.intendedUse.validation.minLength',
                'Value must be at least 10 characters'
              ),
              path: [EddEmbeddedFields.EDD_INTENDED_USE_OTHER_FREE_TEXT],
            });
          } else if (freeText.length > 100) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t(
                'organic.intendedUse.validation.maxLength',
                'Value must be less than 100 characters'
              ),
              path: [EddEmbeddedFields.EDD_INTENDED_USE_OTHER_FREE_TEXT],
            });
          }
        }
      }),
  });

  const handleSubmit = async () => {
    /** Save to local store */
    saveEddQuestionToLocalStore({
      question: t('organic.intendedUse.subtitle'),
      questionId: EddEmbeddedFields.EDD_INTENDED_USE,
      answer: JSON.stringify([formMethods.getValues(EddEmbeddedFields.EDD_INTENDED_USE)]),
    });

    saveEddQuestionToLocalStore({
      question: `${t('organic.intendedUse.subtitle')} - other free text`,
      questionId: EddEmbeddedFields.EDD_INTENDED_USE_OTHER_FREE_TEXT,
      answer: JSON.stringify(
        formMethods.getValues(EddEmbeddedFields.EDD_INTENDED_USE_OTHER_FREE_TEXT)
      ),
    });

    /** Save to Server */
    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_INTENDED_USE);
    } catch (error) {
      /** TODO: Handle error - Check with PM */
      console.error(error);
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
    optionsList,
  };
};
